import type { AnyZod, Inspected } from './introspect'

// Everything date-shaped, in one place: what counts as a valid date input, and what each date declaration
// hands the handler.
//
// The input space is whatever JSON can carry, because every path into the runner is an HTTP body — a real
// `Date` instance is not reachable. That is why `z.date()`, which demands one, could never be satisfied and
// had to be exempted from validation entirely. It is no longer exempt; instead the value is converted for
// CHECKING and the declaration decides what comes out:
//
//   z.date()            the input, unchanged — converting would invent a time of day and a timezone
//   z.coerce.date()     a real Date — zod's own opt-in, and the only honest way to ask for one
//   z.iso.datetime()    an ISO datetime string
//   z.iso.date()        an ISO date string
//   z.iso.time()        an ISO time string
//   z.iso.duration()    strict: a duration is not a point in time, so there is nothing to narrow or widen

// A date-only string, and a datetime whose date half we can take without parsing.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const HAS_TIME = /^(\d{4}-\d{2}-\d{2})[T ](.+)$/
// The trailing zone on an extracted time — `z.iso.time()` rejects it.
const ZONE = /(Z|[+-]\d{2}:?\d{2})$/

const isNumeric = (value: string): boolean => value.trim() !== '' && Number.isFinite(Number(value.trim()))

/**
 * Any date input → a `Date`, or `undefined` when it is not a date at all.
 *
 * A finite number is EPOCH MILLIS, which is what the console's date control stores; a numeric string is that
 * same number through a stringly-typed transport. Everything else that is a string is handed to `Date`.
 *
 * `new Date('nope')` returns a `Date` whose value is `NaN` — and `z.date()` accepts it — so an invalid date
 * has to be rejected here explicitly, or garbage would reach a handler looking like a date.
 */
export function toDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? new Date(value) : undefined
  }

  if (typeof value !== 'string') {
    return undefined
  }

  const parsed = isNumeric(value) ? new Date(Number(value.trim())) : new Date(value)

  return Number.isNaN(parsed.getTime()) ? undefined : parsed
}

/**
 * A date input → the ISO shape the field declared.
 *
 * NARROWING IS LEXICAL. `'2024-06-01T23:00:00Z'` → `'2024-06-01'` takes the characters before the `T`;
 * parsing and reformatting would impose UTC, and in UTC+2 that instant is the 2nd, not the 1st. So a string
 * is sliced, never converted.
 *
 * Two conversions do assume UTC, and both are unavoidable:
 *   • widening a date to a datetime invents a time of day (`'2024-06-01'` → `'2024-06-01T00:00:00Z'`)
 *   • naming a date or a time from EPOCH MILLIS has to pick a wall clock — the same instant is 2024-06-01
 *     in London and 2024-06-02 in Kiritimati
 *
 * `z.iso.datetime()` from millis is not in that class: the instant is preserved and UTC is merely which of
 * several equivalent spellings we emit.
 */
export function toIso(value: unknown, format: string): string | undefined {
  const text = typeof value === 'string' ? value.trim() : undefined
  // A string keeps its own wording wherever the shape can be reached by slicing it.
  const lexical = text && !isNumeric(text) ? sliceIso(text, format) : undefined

  if (lexical) {
    return lexical
  }

  const parsed = toDate(value)

  if (!parsed) {
    return undefined
  }

  const iso = parsed.toISOString()

  if (format === 'datetime') {
    return iso
  }

  if (format === 'date') {
    return iso.slice(0, 10)
  }

  if (format === 'time') {
    return iso.slice(11, 19)
  }

  return undefined
}

// The shape reachable from `text` without any timezone maths. `undefined` when slicing cannot get there.
function sliceIso(text: string, format: string): string | undefined {
  const timed = HAS_TIME.exec(text)

  if (format === 'datetime') {
    // Already a datetime: keep the author's own spelling, offset and precision included.
    return timed ? text : undefined
  }

  if (format === 'date') {
    return timed ? timed[1] : DATE_ONLY.test(text) ? text : undefined
  }

  if (format === 'time') {
    const time = timed ? timed[2] : DATE_ONLY.test(text) ? undefined : text

    return time ? time.replace(ZONE, '') : undefined
  }

  return undefined
}

/** The zod format token a schema declared, if any: 'datetime' | 'date' | 'time' | 'duration' | … */
export const formatOf = (ins: Inspected): string | undefined =>
  (ins.core as { _zod?: { def?: { format?: string } } } | undefined)?._zod?.def?.format

/** The ISO formats this module converts INTO. `duration` is absent on purpose — it stays strict. */
export const isConvertibleIso = (format?: string): boolean =>
  format === 'datetime' || format === 'date' || format === 'time'

/**
 * True for `z.date()` and false for `z.coerce.date()`.
 *
 * `.coerce` is zod's own flag, set by `z.coerce.date()` and readable through the optional/nullable/default
 * wrappers — so the author's intent is a flag to look up rather than a heuristic to guess.
 */
export const passesDateThrough = (ins: Inspected): boolean =>
  ins.coreType === 'date' && !(ins.core as { _zod?: { def?: { coerce?: boolean } } })?._zod?.def?.coerce

/** Whether a schema, at any depth, has a `z.date()` whose value must survive validation unchanged. */
export function hasPassThroughDate(schema: AnyZod | undefined, inspectSchema: (s: AnyZod) => Inspected,
  entriesOf: (s: AnyZod | undefined) => [string, AnyZod][]): boolean {
  if (!schema) {
    return false
  }

  const ins = inspectSchema(schema)

  if (passesDateThrough(ins)) {
    return true
  }

  const element = ins.core?._zod?.def?.element as AnyZod | undefined
  const valueType = ins.core?._zod?.def?.valueType as AnyZod | undefined
  const children = [
    ...entriesOf(ins.core).map(([, child]) => child),
    ...(element ? [element] : []),
    ...(valueType ? [valueType] : []),
    // Every branch of a union, not just the one it resolved to. `z.union([z.number(), z.date()])` coerces
    // through the date branch at runtime, so the restore walk has to know a date is reachable here — or the
    // handler receives the converted `Date` instead of the string the host sent.
    ...(ins.branches ?? []),
  ]

  return children.some(child => hasPassThroughDate(child, inspectSchema, entriesOf))
}
